import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";

import type { MatchEnvMap } from "@acme/ast-grep-wasm-loader";
import type { AstGrepMatch } from "@acme/validators";
import { cn } from "@acme/ui";
import { AccordionItem, AccordionTrigger } from "@acme/ui/accordion";
import { CodeHighlighter } from "@acme/ui/codehike/code-highlighter";

import { useCurrentSource } from "../_hooks/use-studio-store";

export function MatchListItem({
  match,
  isOpen,
  setHoveredMatch,
  hoveredMatch,
  clearSelectedNode,
  onToggle,
  language,
  title,
}: {
  match: AstGrepMatch<MatchEnvMap>;
  isOpen: boolean;
  setHoveredMatch: (match: AstGrepMatch<MatchEnvMap> | null) => void;
  hoveredMatch: AstGrepMatch<MatchEnvMap> | null;
  clearSelectedNode: () => void;
  onToggle: () => void;
  language: string;
  title: string;
}) {
  const [matchStartRow, matchStartCol, matchEndRow, matchEndCol] = match.range;
  const matchEnvArray = Array.from(match.env.entries());
  const source = useCurrentSource();
  const lines = source?.source?.split("\n") ?? [];

  const matchedCode =
    matchStartRow === matchEndRow
      ? lines[matchStartRow]?.substring(matchStartCol, matchEndCol)
      : lines
          .slice(matchStartRow, matchEndRow + 1)
          .map((line, i) => {
            if (i === 0) return line.substring(matchStartCol);
            if (i === matchEndRow - matchStartRow)
              return line.substring(0, matchEndCol);
            return line;
          })
          .join("\n");

  const isHovered = hoveredMatch?.id === match.id;
  const hoverClass = isHovered ? "ring-2 ring-primary" : "";

  const adjustedEnvArray = useMemo(() => {
    return matchEnvArray.map(([key, value]) => {
      const [startRow, startCol, endRow, endCol] = value.range;
      return {
        text: `$${key}`,
        range: [
          startRow - matchStartRow,
          startRow === matchStartRow ? startCol - matchStartCol : startCol,
          endRow - matchStartRow,
          startRow === matchStartRow ? endCol - matchStartCol : endCol,
        ] as const,
      };
    });
  }, [matchEnvArray, matchStartRow, matchStartCol]);

  const [filteredEnvArray, outsideMatch] = useMemo(() => {
    const filteredEnvArray = adjustedEnvArray.filter(({ range }) => {
      const [startRow, startCol, endRow, endCol] = range;
      return startRow >= 1 && startCol >= 1 && endRow >= 1 && endCol >= 1;
    });

    const outsideMatch = adjustedEnvArray.filter(({ range }) => {
      const [startRow, startCol, endRow, endCol] = range;
      return startRow < 1 || startCol < 1 || endRow < 1 || endCol < 1;
    });

    return [filteredEnvArray, outsideMatch];
  }, [adjustedEnvArray]);

  return (
    <AccordionItem
      key={match.id}
      className={cn(
        "border-border bg-background mb-2 cursor-pointer overflow-hidden rounded-lg border transition-all duration-100 dark:bg-black",
        hoverClass,
      )}
      onMouseEnter={() => {
        setHoveredMatch(match);
        clearSelectedNode();
      }}
      onMouseLeave={() => setHoveredMatch(null)}
      value={String(match.id)}
    >
      <AccordionTrigger
        onClick={onToggle}
        className="dark:bg-background relative flex items-center justify-between gap-2 bg-gray-100 p-3 decoration-transparent"
      >
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground/50 text-xs">
            <strong>{title}</strong>
          </span>
          <h2 className="text-primary font-mono text-xs font-medium">
            {match.type === "rule" ? match.rule : "Match"}
          </h2>
        </div>
        {match.type === "rule" ? (
          <>
            {match.severity && (
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 text-xs font-medium",
                  match.severity === "error" &&
                    "bg-destructive/20 text-destructive",
                  match.severity === "warning" && "bg-warning/20 text-warning",
                  match.severity === "info" && "bg-info/20 text-info",
                )}
              >
                {match.severity}
              </span>
            )}

            {match.message && (
              <p className="text-muted-foreground text-sm">{match.message}</p>
            )}
          </>
        ) : null}
        <div className="pattern-wavy pattern-slate-200 pattern-bg-background pattern-size-2 dark:pattern-slate-800 absolute inset-0 border-b" />
      </AccordionTrigger>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key={match.id}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="bg-background"
          >
            <CodeHighlighter
              className="rounded-none border-none"
              code={matchedCode ?? ""}
              language={language}
              callouts={filteredEnvArray}
              lineNumberOffset={matchStartRow}
            />
            {outsideMatch.length > 0 && (
              <div className="border-border/40 text-muted-foreground rounded-b-none border-t bg-neutral-100 p-2 text-xs dark:bg-black/20">
                {outsideMatch.map(({ text }) => (
                  <code
                    key={text}
                    className="bg-muted mx-1 rounded-md px-1 py-0.5 font-mono"
                  >
                    {text}
                  </code>
                ))}
                meta variable{outsideMatch.length > 1 ? "s" : ""} matched
                outside of the code block.
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </AccordionItem>
  );
}
